import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { PermissionService } from "src/services/permissionService/permissionService";
import { useSelector } from "src/store";
import { Sources } from "src/types/osbuilder";

import { useDeleteFileMutation } from "@dashboard/files/api";
import apiClient from "@dashboard/files/api/apiClient";
import { FileInfo, FileStatus } from "@dashboard/files/types/index";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { FileDetailsModal } from "@dashboard/shared/components/MoreInformationFileModal/FileDetailsModal";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { ConfirmDialog, DropdownItem } from "@devices/components/shared";
import DropdownContainer from "@devices/components/shared/Dropdown/DropdownContainer";
import { DialogContentText } from "@mui/material";
import { useCurrentWorkspaceProducts } from "@products/api/helpers";
import { showErrorToast } from "@shared/toasts/Toasts";

interface Props {
    file: FileInfo;
}

const getFileId = (sources: Sources | undefined, sourceId: unknown) => {
    if (typeof sourceId !== "string") {
        return null;
    }
    return sources?.[sourceId]?.fileId ?? null;
};

const DeleteFileItem = (props: Props & { setDeleteFileDialogOpen: (open: boolean) => void }) => {
    const { t } = useTranslation(["common", "modals"]);

    const { groupAbility } = PermissionService();
    const { data: workspace } = useCurrentWorkspace();
    const currentWorkspaceGroups = useSelector(selectGroups);
    const currentGroup = currentWorkspaceGroups.find((item) => item.id === props.file.groupId);
    const mayManageFile = groupAbility(currentGroup || null, workspace).can("manage", "File");

    const { data: products = [] } = useCurrentWorkspaceProducts();

    const isFileUsedInProductBuilder = products.some((product) =>
        product.customBuildConfig?.modules?.some(
            (module) => getFileId(product.customBuildConfig?.sources, module.sourceId) === props.file.id,
        ),
    );

    let tooltipText = null;

    if (!mayManageFile) {
        tooltipText = t("common:section.actionMenu.tooltip.noPermissionToDelete");
    } else if (isFileUsedInProductBuilder) {
        tooltipText = t("common:section.actionMenu.tooltip.usedInProductBuilder");
    }

    return (
        <DropdownItem
            label={t("common:button.delete")}
            iconName="fa-solid fa-fw fa-trash-alt"
            onClick={() => props.setDeleteFileDialogOpen(true)}
            disabled={!mayManageFile || isFileUsedInProductBuilder}
            tooltipProps={{ title: tooltipText }}
        />
    );
};

const DownloadFileItem = (props: Props) => {
    const { t } = useTranslation(["common", "modals", "pages"]);

    const isExpired = useCurrentSubscriptionExpired();
    const isUploading = props.file.status === FileStatus.Pending;
    const isDownloadDisabled = isUploading || isExpired;

    const downloadFile = async () => {
        try {
            await apiClient.invokeFileDownload(props.file.id, props.file.filename);
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        }
    };
    return (
        <DropdownItem
            label={t("common:button.download")}
            tooltipProps={{ title: isExpired ? t("pages:filesPage.tooltips.subscriptionExpired") : null }}
            iconName="fa-solid fa-fw fa-download"
            onClick={downloadFile}
            disabled={isDownloadDisabled}
        />
    );
};

export default (props: Props): React.JSX.Element => {
    const [deleteFileDialogOpen, setDeleteFileDialogOpen] = useState(false);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);

    const anchorRef = React.useRef<HTMLButtonElement | null>(null);

    const { t } = useTranslation(["common", "modals"]);
    const [deleteFile] = useDeleteFileMutation();

    const onDeleteFile = async () => {
        await deleteFile(props.file.id);
    };

    return (
        <React.Fragment>
            <DropdownContainer icon={<i className="fas fa-ellipsis-v" />} anchorRef={anchorRef}>
                <DownloadFileItem file={props.file} />
                <DropdownItem
                    label={t("common:button.edit")}
                    iconName="fa-solid fa-fw fa-pencil"
                    onClick={() => setDetailsModalOpen(true)}
                />
                <DeleteFileItem file={props.file} setDeleteFileDialogOpen={setDeleteFileDialogOpen} />
            </DropdownContainer>

            <FileDetailsModal isOpen={detailsModalOpen} closeDialog={() => setDetailsModalOpen(false)} file={props.file} />

            <ConfirmDialog
                title={t("modals:deleteFile.title")}
                dangerButton
                primaryActionText={t("common:button.delete")}
                open={deleteFileDialogOpen}
                onConfirm={onDeleteFile}
                onClose={() => setDeleteFileDialogOpen(false)}
            >
                <DialogContentText>
                    <Trans
                        ns="modals"
                        i18nKey="deleteFile.description"
                        values={{ filename: props.file.filename }}
                        components={{ bold: <span style={{ fontWeight: "bold" }} /> }}
                    />
                </DialogContentText>
            </ConfirmDialog>
        </React.Fragment>
    );
};
